import { useWhatsappChatsQuery } from '@queries/whatsapp/chats';
import {
  useBroadcastMutation,
  type WhatsappBroadcastStarted,
} from '@queries/whatsapp/broadcast';
import { createSignal, Show } from 'solid-js';

/**
 * Mass-send (群发) a message from one account to many recipients. Recipients can
 * be pasted (one per line) or filled from the account's existing conversations.
 * Sends run throttled in the background and appear live in each chat.
 */
export function BroadcastModal(props: {
  accountId: string | undefined;
  onClose: () => void;
}) {
  const broadcast = useBroadcastMutation();
  const chats = useWhatsappChatsQuery(() => props.accountId, 0);

  const [recipients, setRecipients] = createSignal('');
  const [text, setText] = createSignal('');
  const [result, setResult] = createSignal<WhatsappBroadcastStarted | undefined>();
  const [error, setError] = createSignal<string | undefined>();

  function addFromConversations() {
    const numbers = (chats.data ?? [])
      .map((c) => c.chatJid.split('@')[0])
      .filter((n) => n && !n.includes('-')); // skip group ids
    const existing = new Set(
      recipients()
        .split('\n')
        .map((r) => r.trim())
        .filter(Boolean)
    );
    for (const n of numbers) existing.add(n);
    setRecipients([...existing].join('\n'));
  }

  async function submit() {
    setError(undefined);
    setResult(undefined);
    const account = props.accountId;
    if (!account) {
      setError('Select an account first.');
      return;
    }
    const list = recipients()
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean);
    if (list.length === 0) {
      setError('Add at least one recipient.');
      return;
    }
    if (!text().trim()) {
      setError('Enter a message.');
      return;
    }
    try {
      setResult(
        await broadcast.mutateAsync({
          accountId: account,
          recipients: list,
          text: text(),
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const count = () =>
    recipients()
      .split('\n')
      .map((r) => r.trim())
      .filter(Boolean).length;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={props.onClose}
    >
      <div
        class="flex max-h-[90vh] w-[460px] flex-col rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Broadcast (群发)</h2>
          <button
            class="text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>

        <div class="mb-2 flex items-center justify-between">
          <label class="text-sm font-medium">Recipients ({count()})</label>
          <button
            class="text-xs text-green-600 hover:underline disabled:opacity-50"
            disabled={!props.accountId || (chats.data ?? []).length === 0}
            onClick={addFromConversations}
          >
            + Add from my conversations
          </button>
        </div>
        <textarea
          class="mb-3 h-28 w-full resize-none rounded border border-neutral-300 bg-transparent p-2 text-sm dark:border-neutral-700"
          placeholder="One phone number per line, e.g.&#10;14155551234&#10;447700900123"
          value={recipients()}
          onInput={(e) => setRecipients(e.currentTarget.value)}
        />

        <label class="mb-2 text-sm font-medium">Message</label>
        <textarea
          class="mb-3 h-24 w-full resize-none rounded border border-neutral-300 bg-transparent p-2 text-sm dark:border-neutral-700"
          placeholder="Your message…"
          value={text()}
          onInput={(e) => setText(e.currentTarget.value)}
        />

        <Show when={error()}>
          <p class="mb-2 text-sm text-red-500">{error()}</p>
        </Show>
        <Show when={result()}>
          <p class="mb-2 text-sm text-green-600">
            Sending to {result()!.total} recipient(s)
            {result()!.skipped > 0
              ? ` (${result()!.skipped} skipped)`
              : ''}
            . They'll appear in your conversations as they go out.
          </p>
        </Show>

        <div class="mt-1 flex items-center justify-between">
          <p class="text-xs text-neutral-500">
            Throttled to reduce anti-spam risk.
          </p>
          <button
            class="rounded bg-green-600 px-4 py-1.5 text-sm text-white disabled:opacity-50"
            disabled={broadcast.isPending || !props.accountId}
            onClick={() => void submit()}
          >
            {broadcast.isPending ? 'Starting…' : 'Send broadcast'}
          </button>
        </div>
      </div>
    </div>
  );
}
